import React, { useCallback, useMemo } from "react";
import { Box, CircularProgress } from "@material-ui/core";
import { format } from "date-fns";
import { T, useT } from "@transifex/react";
import { Modal } from "storybook/components/modal";
import { Button } from "storybook/components/button";
import { Typography } from "storybook/components/typography";
import { ErrorType, usePatientInterest } from "context/patient-interest";
import { ErrorBanner, ConfirmButtons, ModalBodyWrapper } from "../styles";
import { FormSection } from ".";
import type { AvailabilityFormData } from "./availability-form";

type Props = {
  dates?: Date[];
  availability?: AvailabilityFormData;
  onEdit: () => void;
  onClose: () => void;
  onContinue: (data: unknown, formSection: FormSection) => void;
  isSubmitting: boolean;
};

export function Confirmation({
  onEdit,
  onClose,
  onContinue,
  dates = [],
  availability,
  isSubmitting,
}: Props) {
  const t = useT();
  const { errorType } = usePatientInterest();

  const handleConfirm = useCallback(() => {
    onContinue(null, FormSection.CONFIRMATION);
  }, [onContinue]);
  
  const formatAvailabilityForDisplay = useCallback((availability: AvailabilityFormData): string[] => {
    const lines: string[] = [];
    const DAY_LABELS: Record<keyof Pick<AvailabilityFormData, "monday" | "tuesday" | "wednesday" | "thursday" | "friday">, string> = {
      monday: t("Monday"),
      tuesday: t("Tuesday"),
      wednesday: t("Wednesday"),
      thursday: t("Thursday"),
      friday: t("Friday"),
    };

    if (availability.anytime) {
      return [t("Flexible - Available Anytime (Mon–Fri)")];
    }

    const dayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday"] as const;

    for (const key of dayKeys) {
      const day = availability[key];
      if (!day?.enabled) continue;
      const parts: string[] = [];
      if (day.am) parts.push(t("AM"));
      if (day.pm) parts.push(t("PM"));
      if (parts.length) {
        lines.push(`${DAY_LABELS[key]}: ${parts.join(", ")}`);
      }
    }

    return lines;
  }, [t]);
  
  const availabilityLines = useMemo(
    () => (availability ? formatAvailabilityForDisplay(availability) : []),
    [availability, formatAvailabilityForDisplay]
  );

  const formattedDates = useMemo(
    () =>
      dates.map((date) =>
        format(date, "EEEE, MMM d, yyyy 'at' h:mmaaa zzz")
      ),
    [dates]
  );

  return (
    <>
      <Modal.Body>
        <ModalBodyWrapper>
          {errorType === ErrorType.GENERAL && (
            <ErrorBanner role="alert">
              <T _str="Something went wrong, please try again" />
            </ErrorBanner>
          )}
          <Box mb="24px">
            <Typography theme="default" variant="body1" color="black">
              {availabilityLines.length > 0 ? (
                <T _str="Thank you for letting us know your preferred time(s) for an appointment!" />
              ) : (
                <T _str="Thank you for letting us know your preferred date(s) and time(s) for an appointment!" />
              )}
            </Typography>
          </Box>
          <Typography theme="default" variant="body1" color="ciqBrand">
            <T _str="You said you were available on:" />
          </Typography>
          {availabilityLines.length > 0 && (
            <ul>
              {availabilityLines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          )}
          {!availabilityLines.length && formattedDates.length > 0 && (
            <ul>
              {formattedDates.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </ModalBodyWrapper>
      </Modal.Body>
      <Modal.Actions alignButtons="center">
        {isSubmitting ? (
          <CircularProgress />
        ) : (
          <ConfirmButtons>
            <Button
              color="default"
              variant="text"
              theme="default"
              onClick={onClose}
              fullWidth={true}
            >
              <T _str="Cancel" />
            </Button>
            <Button
              color="secondary"
              variant="contained"
              theme="default"
              fullWidth={true}
              onClick={onEdit}
            >
              <T _str="Edit" />
            </Button>
            <Button
              color="default"
              variant="contained"
              theme="default"
              fullWidth={true}
              onClick={handleConfirm}
            >
              <T _str="Confirm" />
            </Button>
          </ConfirmButtons>
        )}
      </Modal.Actions>
    </>
  );
}
